import React from 'react';
import { Check, type Icon } from '@phosphor-icons/react';
import { ImmoButton } from './Button';

/**
 * Schritt-Formular auf der Rückseite einer Drehkarte (README §9):
 * nummerierte Kreise (erledigt = Haken auf --ok), Fortschrittsbalken 5 px --ok,
 * Speichern bei opacity .45, bis die Pflichtfelder gefüllt sind; Live-Vorschau rechts.
 */
export interface Schritt {
  titel: string;
  untertitel?: string;
  erledigt: boolean;
  inhalt: React.ReactNode;
}

export interface StepFormProps {
  schritte: readonly Schritt[];
  /** Hinweis über dem Balken, z. B. „Noch offen: Wohnfläche“ / „Alles bereit“. */
  hinweis: string;
  bereit: boolean;
  onSpeichern: () => void;
  speichernLabel: string;
  speichernIcon?: Icon;
  /** Läuft gerade ein Speichervorgang? */
  speichert?: boolean;
  /** Zusätzlicher Knopf links vom Speichern (z. B. „Abbrechen“). */
  sekundaer?: React.ReactNode;
  /** Live-Vorschau rechts (LivePreviewCard). */
  vorschau?: React.ReactNode;
}

export function StepForm({ schritte, hinweis, bereit, onSpeichern, speichernLabel, speichernIcon = Check, speichert = false, sekundaer, vorschau }: StepFormProps) {
  const erledigt = schritte.filter(s => s.erledigt).length;
  const fortschritt = schritte.length ? erledigt / schritte.length : 0;
  const ersterOffen = schritte.findIndex(s => !s.erledigt);

  return (
    <div className="flex flex-wrap items-start" style={{ gap: 16 }}>
      <form
        onSubmit={e => { e.preventDefault(); if (bereit && !speichert) onSpeichern(); }}
        className="immo-innen flex flex-col min-w-0"
        style={{ flex: '1 1 440px', borderRadius: 26, padding: 20, gap: 8 }}
      >
        {schritte.map((s, i) => {
          const aktuell = i === ersterOffen;
          const kreis: React.CSSProperties = s.erledigt
            ? { background: 'var(--ok)', color: '#fff' }
            : aktuell
              ? { background: 'var(--acc-fill)', color: 'var(--acc-ink)' }
              : { background: 'var(--fill2)', color: 'var(--ink2)' };
          return (
            <div key={s.titel} className="flex" style={{ gap: 14 }}>
              <div className="flex flex-col items-center shrink-0" style={{ gap: 6 }}>
                <span
                  aria-label={s.erledigt ? `Schritt ${i + 1} erledigt` : `Schritt ${i + 1}`}
                  className="flex items-center justify-center"
                  style={{ width: 28, height: 28, borderRadius: '50%', fontSize: 13, fontWeight: 800, transition: 'background .3s', ...kreis }}
                >
                  {s.erledigt ? <Check weight="bold" size={13} /> : i + 1}
                </span>
                <span style={{ flex: 1, width: 2, borderRadius: 1, background: 'var(--line)' }} />
              </div>
              <div className="flex flex-col flex-1 min-w-0" style={{ gap: 12, paddingBottom: 6 }}>
                <div className="flex flex-col" style={{ gap: 1 }}>
                  <span style={{ fontSize: 15, fontWeight: 800 }}>{s.titel}</span>
                  {s.untertitel && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{s.untertitel}</span>}
                </div>
                {s.inhalt}
              </div>
            </div>
          );
        })}
        <div className="flex items-center flex-wrap" style={{ gap: 12, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <div className="flex flex-col" style={{ flex: '1 1 180px', gap: 6 }}>
            <span aria-live="polite" style={{ fontSize: 12, fontWeight: 700, color: bereit ? 'var(--ok)' : 'var(--ink3)' }}>{hinweis}</span>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(fortschritt * 100)}
              style={{ height: 5, borderRadius: 3, background: 'var(--fill2)', overflow: 'hidden' }}
            >
              <div style={{ height: '100%', width: `${fortschritt * 100}%`, borderRadius: 3, background: 'var(--ok)', transition: 'width .3s' }} />
            </div>
          </div>
          {sekundaer}
          <ImmoButton type="submit" art="akzent" groesse={48} pille={false} icon={speichernIcon} disabled={!bereit || speichert}>
            {speichernLabel}
          </ImmoButton>
        </div>
      </form>
      {vorschau}
    </div>
  );
}
